// assets/featured-collection-tabs.js
(() => {
  class FeaturedCollectionTabs {
    constructor(sectionEl) {
      this.sectionEl = sectionEl;

      // Tabs header container
      this.tabsRoot = sectionEl.querySelector('[data-tabs]');
      if (!this.tabsRoot) return;

      this.triggers = Array.from(this.tabsRoot.querySelectorAll('[data-tab-trigger]'));
      this.panels = Array.from(sectionEl.querySelectorAll('[data-tab-panel]'));
      this.discoverLink = sectionEl.querySelector('[data-discover-link]');

      if (!this.triggers.length || !this.panels.length) return;

      // Click handlers
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.triggers.forEach((btn) => {
        btn.addEventListener('click', this.onClick);
        btn.addEventListener('keydown', this.onKeyDown);
      });

      // Ensure initial state is coherent
      const active = this.triggers.find((b) => b.classList.contains('is-active')) || this.triggers[0];
      this.setActive(active?.dataset?.tabTrigger || '1', { focus: false });
    }

    onClick(e) {
      const btn = e.currentTarget;
      this.setActive(btn.dataset.tabTrigger, { focus: false });
    }

    // Keyboard: left/right/home/end
    onKeyDown(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(e.key)) return;

      e.preventDefault();

      const currentIndex = this.triggers.indexOf(e.currentTarget);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
      if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.triggers.length;
      if (e.key === 'Home') nextIndex = 0;
      if (e.key === 'End') nextIndex = this.triggers.length - 1;

      const next = this.triggers[nextIndex];
      this.setActive(next.dataset.tabTrigger, { focus: true });
    }

    setActive(tabId, { focus } = { focus: false }) {
      // Update triggers
      this.triggers.forEach((btn) => {
        const isActive = btn.dataset.tabTrigger === String(tabId);
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        // Roving tabindex for accessibility
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && focus) btn.focus();
      });

      // Update panels
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === String(tabId);
        panel.toggleAttribute('hidden', !isActive);

        // If your slider component needs recalculation when shown:
        if (isActive) this.refreshSlider(panel);
      });

      // Update discover link URL to the active collection
      const activeBtn = this.triggers.find((b) => b.dataset.tabTrigger === String(tabId));
      const url = activeBtn?.dataset?.collectionUrl;
      if (this.discoverLink && url) this.discoverLink.setAttribute('href', url);
    }

    refreshSlider(panel) {
      // Dawn's slider-component reads sizes; when a panel was hidden, widths may be 0.
      // We gently nudge it by dispatching resize.
      // This is lightweight and works well with Dawn.
      window.dispatchEvent(new Event('resize'));

      // Optional: reset to first slide when switching tabs
      const slider = panel.querySelector('slider-component');
      const ul = panel.querySelector('.slider');
      if (!slider || !ul) return;

      // Try to scroll back to start
      ul.scrollTo({ left: 0, behavior: 'auto' });
    }

    destroy() {
      this.triggers.forEach((btn) => {
        btn.removeEventListener('click', this.onClick);
        btn.removeEventListener('keydown', this.onKeyDown);
      });
    }
  }

  // Init on normal page load
  const initAll = (root = document) => {
    root.querySelectorAll('[data-section-type="featured-collection-tabs"]').forEach((section) => {
      // avoid double init
      if (section.__featuredCollectionTabs) return;
      section.__featuredCollectionTabs = new FeaturedCollectionTabs(section);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initAll());

  // Shopify Theme Editor: re-init on section load/unload
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (section?.__featuredCollectionTabs) {
      section.__featuredCollectionTabs.destroy();
      delete section.__featuredCollectionTabs;
    }
  });
})();
